/**
 * BankFeedUploadsTab - extracted from BankFeedCenterPage Phase 2.
 * Renders the uploaded-statements list (with per-row download / remove).
 * iter1262 - bank + date-range filters and centered Prev/Next pagination.
 */
import React, { useMemo, useState } from 'react';
import { openMissingRowsDialog } from './MissingRowsDialog';
import { ConfirmNoActivityButton } from './ConfirmNoActivityButton';
import { GapRemindersStrip } from './GapRemindersStrip';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { isIOSNative } from '../../utils/nativeBridge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  FileUp,
  FileText,
  Upload,
  Loader2,
  X,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const UPLOADS_PAGE_SIZE = 10;

export const BankFeedUploadsTab = ({
  uploadedStatements = [],
  fileInputRef,
  onOpenUploadStarter,
  handleDeleteStatement,
  deletingStatementId,
  handleDownloadStatement,
  downloadingStatementId,
  bankAccounts = [],
  onStatementResumed,
}) => {
  const [bankFilter, setBankFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);

  const stmtBank = (s) => s.bank_account_name || s.bank_name || 'Unknown Account';
  const bankOptions = useMemo(
    () => [...new Set(uploadedStatements.map(stmtBank))].sort(),
    [uploadedStatements]
  );

  const filtered = useMemo(() => uploadedStatements.filter((s) => {
    if (bankFilter !== 'all' && stmtBank(s) !== bankFilter) return false;
    const d = s.uploaded_at ? String(s.uploaded_at).slice(0, 10) : '';
    if (startDate && d && d < startDate) return false;
    if (endDate && d && d > endDate) return false;
    return true;
  }), [uploadedStatements, bankFilter, startDate, endDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / UPLOADS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * UPLOADS_PAGE_SIZE, safePage * UPLOADS_PAGE_SIZE);
  const hasFilters = bankFilter !== 'all' || startDate || endDate;

  return (
    <>
      <GapRemindersStrip bankAccounts={bankAccounts} onUpload={() => onOpenUploadStarter?.()} />
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileUp className="w-5 h-5 text-[#108981]" />
            Uploaded Bank Statements
          </CardTitle>
          <CardDescription>
            Manage your uploaded statements. Remove an upload to reverse all associated journal entries.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {uploadedStatements.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4" data-testid="uploads-filter-bar">
              <Select value={bankFilter} onValueChange={(v) => { setBankFilter(v); setPage(1); }}>
                <SelectTrigger className="sm:w-56" data-testid="uploads-bank-filter">
                  <SelectValue placeholder="All banks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All banks</SelectItem>
                  {bankOptions.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="flex-1 min-w-0 sm:flex-none sm:w-40"
                  title="Uploaded from"
                  data-testid="uploads-start-date-filter"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="flex-1 min-w-0 sm:flex-none sm:w-40"
                  title="Uploaded until"
                  data-testid="uploads-end-date-filter"
                />
              </div>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setBankFilter('all'); setStartDate(''); setEndDate(''); setPage(1); }}
                  data-testid="uploads-clear-filters"
                >
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
              <p className="text-xs text-muted-foreground sm:ml-auto" data-testid="uploads-filter-count">
                {filtered.length} of {uploadedStatements.length} uploads
              </p>
            </div>
          )}
          {uploadedStatements.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <FileUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">Upload a bank statement or connect your bank to get started</p>
              <Button
                onClick={onOpenUploadStarter || (() => fileInputRef?.current?.click())}
                className="gap-2 bg-[#108981] hover:bg-[#0d6b65]"
                data-testid="uploads-tab-first-upload-btn"
              >
                <Upload className="w-4 h-4" />
                Upload Your First Statement
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed rounded-lg" data-testid="uploads-no-match">
              <p className="text-muted-foreground">No uploads match your filters.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pageItems.map((statement) => (
                <motion.div
                  key={statement.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                  data-testid={`statement-item-${statement.id}`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg shrink-0 ${
                      statement.file_type === 'pdf' ? 'bg-red-100' :
                      statement.file_type === 'xlsx' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <FileText className={`w-5 h-5 ${
                        statement.file_type === 'pdf' ? 'text-red-600' :
                        statement.file_type === 'xlsx' ? 'text-green-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={statement.filename}>
                        {statement.filename}
                      </p>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap">
                        <span className="truncate">{statement.bank_account_name || 'Unknown Account'}</span>
                        <span>•</span>
                        <span>{new Date(statement.uploaded_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                        {statement.period_start && statement.period_end && (
                          <>
                            <span>•</span>
                            <span title="Statement period" data-testid={`stmt-period-${statement.id}`}>
                              {new Date(statement.period_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              {' → '}
                              {new Date(statement.period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap mt-1">
                        {statement.status === 'blocked_continuity' && (
                          <span
                            className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium"
                            title={statement.status_message || 'Blocked by the statement continuity check'}
                            data-testid={`stmt-blocked-${statement.id}`}
                          >
                            Blocked — {statement.status_message || 'gap in statement dates. Upload the missing period first.'}
                          </span>
                        )}
                        {statement.status === 'blocked_continuity' && statement.continuity_block?.can_confirm_no_activity && (
                          <ConfirmNoActivityButton statement={statement} onResumed={onStatementResumed} />
                        )}
                        {statement.status === 'blocked_continuity' && statement.continuity_block?.reason !== 'first_upload_after_start_date' && (
                          <button
                            type="button"
                            onClick={() => openMissingRowsDialog(statement.job_id || statement.id, statement.filename)}
                            className="px-1.5 py-0.5 rounded border border-[#283F8B]/30 text-[#283F8B] hover:bg-[#283F8B]/5 font-medium"
                            data-testid={`stmt-missing-rows-${statement.id}`}
                          >
                            Review missing rows
                          </button>
                        )}
                        {statement.backlog_free ? (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`stmt-backlog-free-${statement.id}`}>
                            Free upload
                          </span>
                        ) : statement.backlog_fee_charged > 0 && !isIOSNative() ? (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`stmt-backlog-fee-${statement.id}`}>
                            Backlog {statement.backlog_symbol || ''}{Number(statement.backlog_fee_charged).toLocaleString()} charged
                          </span>
                        ) : null}
                        {statement.confirm_mode === 'current_only' && (
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                            Current only
                          </span>
                        )}
                        {statement.continuity_case === 'continuation_verified' && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" title="Balance verified against previous statement">
                            ✓ Continuity verified
                          </span>
                        )}
                        {statement.integrity_warning && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800" title={statement.integrity_warning} data-testid={`stmt-integrity-warning-${statement.id}`}>
                            ⚠ Balance check flagged
                          </span>
                        )}
                        {statement.continuity_case === 'overlap_forward_verified' && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" title="Overlap trimmed; balance verified">
                            ✓ Overlap trimmed
                          </span>
                        )}
                        {statement.closing_balance_recorded != null && (
                          <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700" title="Books closing balance after this upload" data-testid={`stmt-closing-${statement.id}`}>
                            Closing {(statement.backlog_symbol || '')}{Number(statement.closing_balance_recorded).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-6 text-sm">
                    <div className="text-left sm:text-center">
                      <p className="font-semibold text-[#108981]">{statement.transactions_count || 0}</p>
                      <p className="text-muted-foreground text-xs">Transactions</p>
                    </div>
                    <div className="text-left sm:text-center" data-testid={`stmt-in-books-${statement.id}`}>
                      <p className="font-semibold text-blue-600">{statement.processed_count || 0}</p>
                      <p className="text-muted-foreground text-xs">In Books</p>
                    </div>
                    {(statement.duplicates_count || 0) > 0 && (
                      <div className="text-left sm:text-center">
                        <p className="font-semibold text-amber-600">{statement.duplicates_count}</p>
                        <p className="text-muted-foreground text-xs">Duplicates</p>
                      </div>
                    )}
                    <div className="text-left sm:text-center" data-testid={`stmt-review-${statement.id}`}>
                      <p className={`font-semibold ${(statement.review_needed_count || 0) > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                        {statement.review_needed_count || 0}
                      </p>
                      <p className="text-muted-foreground text-xs">Review Needed</p>
                    </div>
                    <div className="flex items-center gap-1 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-2 sm:pt-0 mt-1 sm:mt-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-600 hover:text-slate-800 hover:bg-slate-100 shrink-0"
                        onClick={() => handleDownloadStatement && handleDownloadStatement(statement)}
                        disabled={downloadingStatementId === statement.id}
                        title="Download the original uploaded file"
                        data-testid={`download-statement-${statement.id}`}
                      >
                        {downloadingStatementId === statement.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => handleDeleteStatement(statement.id, statement.filename)}
                        disabled={deletingStatementId === statement.id}
                        data-testid={`delete-statement-${statement.id}`}
                      >
                        {deletingStatementId === statement.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
              {totalPages > 1 && (
                <div className="flex flex-col sm:grid sm:grid-cols-[1fr_auto_1fr] items-center gap-2 border-t pt-4 mt-4 text-xs text-muted-foreground" data-testid="uploads-pagination">
                  <span className="sm:justify-self-start">
                    Showing {(safePage - 1) * UPLOADS_PAGE_SIZE + 1}–{Math.min(safePage * UPLOADS_PAGE_SIZE, filtered.length)} of {filtered.length}
                  </span>
                  <div className="flex items-center gap-2 sm:justify-self-center">
                    <Button size="sm" variant="outline" className="h-7 px-2" disabled={safePage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="uploads-prev-page">
                      <ChevronLeft className="w-3.5 h-3.5" /> Prev
                    </Button>
                    <span className="tabular-nums">Page {safePage} / {totalPages}</span>
                    <Button size="sm" variant="outline" className="h-7 px-2" disabled={safePage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="uploads-next-page">
                      Next <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="hidden sm:block" />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

    </>
  );
};

export default BankFeedUploadsTab;
